import { useEffect, useState } from 'react'

/**
 * Whether the element shows a scrollbar that takes up width. A floating (overlay)
 * scrollbar, the macOS trackpad default, takes none, so it reads as false there.
 *
 * Re-measured whenever the element or one of its children changes size, which covers
 * comments being added, removed or expanded. A child swapped for another (the empty
 * state for the list) is not observed, so the caller passes a contentKey that changes
 * with it and re-attaches the observer.
 * @param {{ current: HTMLElement | null }} ref - the scrolling element
 * @param {string} contentKey - changes whenever the element's children are replaced
 * @returns {boolean}
 */
export default function useHasScrollbar(ref, contentKey) {
  const [hasScrollbar, setHasScrollbar] = useState(false)

  useEffect(() => {
    const element = ref.current

    if (!element || typeof ResizeObserver === 'undefined') {
      setHasScrollbar(false)

      return undefined
    }

    const measure = () => setHasScrollbar(element.offsetWidth > element.clientWidth)
    const observer = new ResizeObserver(measure)

    observer.observe(element)
    Array.from(element.children).forEach(child => observer.observe(child))
    measure()

    return () => observer.disconnect()
  }, [ref, contentKey])

  return hasScrollbar
}
